import React, { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../context/useAuth'
import { api } from '../api/client'
import PageShell from '../components/ui/PageShell'
import Card from '../components/ui/Card'
import { PrimaryButton, SecondaryButton, DangerButton } from '../components/ui/Button'

export default function Admin() {
  const { user } = useAuth()
  const [users, setUsers] = useState([])
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [ledger, setLedger] = useState([])
  const [creditsDelta, setCreditsDelta] = useState('')
  const [creditsSet, setCreditsSet] = useState('')
  const [unlimited, setUnlimited] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [settings, setSettings] = useState({ default_credits_new_user: 3, job_cost_credits: 1, cover_cost_credits: 0, canvas_cost_credits: 0, enable_billing: false })
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [settingsMessage, setSettingsMessage] = useState('')
  const [products, setProducts] = useState([])
  const [productsLoading, setProductsLoading] = useState(false)
  const [productForm, setProductForm] = useState(null)
  const [productSaving, setProductSaving] = useState(false)

  useEffect(() => {
    api.get('/admin/settings').then((r) => setSettings(r.data || {})).catch(() => {})
  }, [])

  const loadProducts = () => {
    setProductsLoading(true)
    api.get('/admin/credit-products').then((r) => setProducts(r.data?.products || [])).catch(() => setProducts([])).finally(() => setProductsLoading(false))
  }
  useEffect(() => { loadProducts() }, [])

  const saveSettings = () => {
    const defaultCredits = parseInt(settings.default_credits_new_user, 10)
    const jobCost = parseInt(settings.job_cost_credits, 10)
    if (isNaN(defaultCredits) || defaultCredits < 0 || isNaN(jobCost) || jobCost < 1) {
      setSettingsMessage('Ungültige Werte (Startguthaben ≥ 0, Credits pro Job ≥ 1)')
      return
    }
    setSettingsSaving(true)
    setSettingsMessage('')
    const coverCost = parseInt(settings.cover_cost_credits, 10)
    const canvasCost = parseInt(settings.canvas_cost_credits, 10)
    if (isNaN(coverCost) || coverCost < 0 || isNaN(canvasCost) || canvasCost < 0) {
      setSettingsMessage('Cover- und Canvas-Credits müssen ≥ 0 sein.')
      setSettingsSaving(false)
      return
    }
    api.patch('/admin/settings', {
      default_credits_new_user: defaultCredits,
      job_cost_credits: jobCost,
      cover_cost_credits: coverCost,
      canvas_cost_credits: canvasCost,
      enable_billing: !!settings.enable_billing,
    })
      .then((r) => {
        setSettings(r.data || settings)
        setSettingsMessage('Gespeichert.')
      })
      .catch((e) => setSettingsMessage(e?.message || 'Fehler'))
      .finally(() => setSettingsSaving(false))
  }

  const saveProduct = () => {
    if (!productForm) return
    const { id, name, credits, price_cents, currency, stripe_price_id, sort_order, active } = productForm
    setProductSaving(true)
    const body = { name: name?.trim(), credits: parseInt(credits, 10), price_cents: parseInt(price_cents, 10), currency: currency || 'eur', stripe_price_id: stripe_price_id?.trim() || null, sort_order: parseInt(sort_order, 10) || 0, active: !!active }
    if (id) {
      api.patch(`/admin/credit-products/${id}`, body).then(() => { setProductForm(null); loadProducts() }).catch((e) => setSettingsMessage(e?.message || 'Fehler')).finally(() => setProductSaving(false))
    } else {
      api.post('/admin/credit-products', body).then(() => { setProductForm(null); loadProducts() }).catch((e) => setSettingsMessage(e?.message || 'Fehler')).finally(() => setProductSaving(false))
    }
  }
  const deleteProduct = (id) => {
    if (!window.confirm('Paket wirklich löschen?')) return
    api.delete(`/admin/credit-products/${id}`).then(() => loadProducts()).catch((e) => setSettingsMessage(e?.message || 'Fehler'))
  }

  const loadUsers = () => {
    setLoading(true)
    api.get(`/admin/users/search?q=${encodeURIComponent(search)}`)
      .then((r) => setUsers(r.data?.users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadUsers()
  }, [search])

  const selectUser = (u) => {
    setSelected(u)
    setCreditsDelta('')
    setCreditsSet('')
    setUnlimited(u?.credits_unlimited ?? false)
    if (!u) {
      setLedger([])
      return
    }
    api.get(`/admin/users/${u.id}/ledger`)
      .then((r) => setLedger(r.data?.entries || []))
      .catch(() => setLedger([]))
  }

  const applyDelta = (d) => {
    if (!selected) return
    const n = parseInt(d, 10)
    if (isNaN(n)) return
    setMessage('')
    api.patch(`/admin/users/${selected.id}/credits`, { delta: n })
      .then((r) => {
        setSelected({ ...selected, credits_balance: r.data?.credits_balance ?? selected.credits_balance })
        setCreditsDelta('')
        loadUsers()
        setMessage(`${n > 0 ? '+' : ''}${n} Credits angewendet`)
      })
      .catch((e) => setMessage(e?.message || 'Fehler'))
  }

  const applySet = () => {
    if (!selected) return
    const n = parseInt(creditsSet, 10)
    if (isNaN(n)) return
    setMessage('')
    api.patch(`/admin/users/${selected.id}/credits`, { balance: n })
      .then((r) => {
        setSelected({ ...selected, credits_balance: r.data?.credits_balance ?? n })
        setCreditsSet('')
        loadUsers()
        setMessage(`Kontostand auf ${n} gesetzt`)
      })
      .catch((e) => setMessage(e?.message || 'Fehler'))
  }

  const toggleUnlimited = () => {
    if (!selected) return
    setMessage('')
    api.patch(`/admin/users/${selected.id}/credits`, { unlimited: !unlimited })
      .then((r) => {
        setUnlimited(r.data?.credits_unlimited ?? !unlimited)
        setSelected({ ...selected, credits_unlimited: r.data?.credits_unlimited })
        loadUsers()
        setMessage(unlimited ? 'Unlimited deaktiviert' : 'Unlimited aktiviert')
      })
      .catch((e) => setMessage(e?.message || 'Fehler'))
  }

  const toggleSperren = () => {
    if (!selected) return
    const newActive = !selected.is_active
    setMessage('')
    api.patch(`/users/${selected.id}`, { is_active: newActive })
      .then((r) => {
        setSelected({ ...selected, is_active: r.data?.is_active ?? newActive })
        loadUsers()
        setMessage(newActive ? 'Nutzer entsperrt' : 'Nutzer gesperrt')
      })
      .catch((e) => setMessage(e?.message || 'Fehler'))
  }

  if (!user?.is_admin) return <Navigate to="/dashboard" replace />

  return (
    <PageShell>
      <div className="mx-auto max-w-5xl space-y-6">
        <h1 className="text-2xl font-bold text-white">Admin – Nutzer & Credits</h1>

        <Card className="p-4">
          <h2 className="font-semibold text-white mb-4">Credits & Preise</h2>
          <p className="text-sm text-white/60 mb-4">Preise hier eintragen – sie gelten sofort. Nutzer kaufen Credits unter <strong>„Credits bestellen“</strong> (Navigation), sofern die Zahlungsschnittstelle aktiv ist.</p>
          <div className="flex flex-wrap gap-6 items-end">
            <div>
              <label className="block text-xs text-white/60 mb-1">Startguthaben bei Registrierung</label>
              <input
                type="number"
                min={0}
                value={settings.default_credits_new_user ?? 3}
                onChange={(e) => setSettings((s) => ({ ...s, default_credits_new_user: e.target.value }))}
                className="w-24 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">Credits pro Mastering-Job</label>
              <input
                type="number"
                min={1}
                value={settings.job_cost_credits ?? 1}
                onChange={(e) => setSettings((s) => ({ ...s, job_cost_credits: e.target.value }))}
                className="w-24 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">Credits pro Cover (Bild)</label>
              <input
                type="number"
                min={0}
                value={settings.cover_cost_credits ?? 0}
                onChange={(e) => setSettings((s) => ({ ...s, cover_cost_credits: e.target.value }))}
                className="w-24 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white"
                title="0 = kostenlos"
              />
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">Credits pro Canvas</label>
              <input
                type="number"
                min={0}
                value={settings.canvas_cost_credits ?? 0}
                onChange={(e) => setSettings((s) => ({ ...s, canvas_cost_credits: e.target.value }))}
                className="w-24 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white"
                title="0 = kostenlos"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!settings.enable_billing}
                onChange={(e) => setSettings((s) => ({ ...s, enable_billing: e.target.checked }))}
                className="rounded border-white/20 bg-white/5 text-violet-500"
              />
              <span className="text-sm text-white/80">Zahlungsschnittstelle aktiv (Stripe API → Nutzer können unter „Credits bestellen“ aufladen)</span>
            </label>
            <PrimaryButton onClick={saveSettings} disabled={settingsSaving}>
              {settingsSaving ? 'Speichern…' : 'Speichern'}
            </PrimaryButton>
          </div>
          {settingsMessage && <p className={`mt-3 text-sm ${settingsMessage === 'Gespeichert.' ? 'text-green-400' : 'text-amber-400'}`}>{settingsMessage}</p>}
        </Card>

        <Card className="p-4">
          <h2 className="font-semibold text-white mb-2">Credit-Pakete (Zahlungsschnittstelle)</h2>
          <p className="text-sm text-white/60 mb-4">Pakete und Preise für <strong>„Credits bestellen“</strong> (in der Nutzer-Navigation). Wenn oben „Zahlungsschnittstelle aktiv“ gesetzt und Stripe in der .env konfiguriert ist, können Nutzer dort Credits kaufen und werden automatisch gutgeschrieben.</p>
          {productsLoading && <p className="text-sm text-white/60">Laden…</p>}
          {!productsLoading && (
            <>
              <ul className="space-y-2 mb-4">
                {products.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-3 rounded-lg bg-white/5 px-3 py-2">
                    <span className="text-white font-medium">{p.name}</span>
                    <span className="text-white/60">{p.credits} Credits</span>
                    <span className="text-white/60">{(p.price_cents / 100).toFixed(2)} {p.currency?.toUpperCase()}</span>
                    {p.stripe_price_id && <span className="text-xs text-white/40">Stripe: {p.stripe_price_id}</span>}
                    {!p.active && <span className="text-amber-400 text-sm">inaktiv</span>}
                    <div className="ml-auto flex gap-2">
                      <SecondaryButton onClick={() => setProductForm({ ...p })}>Bearbeiten</SecondaryButton>
                      <DangerButton onClick={() => deleteProduct(p.id)}>Löschen</DangerButton>
                    </div>
                  </li>
                ))}
              </ul>
              <PrimaryButton onClick={() => setProductForm({ name: '', credits: 10, price_cents: 499, currency: 'eur', stripe_price_id: '', sort_order: 0, active: true })}>
                Paket anlegen
              </PrimaryButton>
            </>
          )}
          {productForm && (
            <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
              <h3 className="text-white font-medium mb-3">{productForm.id ? 'Paket bearbeiten' : 'Neues Paket'}</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="block text-xs text-white/60 mb-1">Name (z.B. „10 Credits“)</span>
                  <input type="text" value={productForm.name ?? ''} onChange={(e) => setProductForm((f) => ({ ...f, name: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white" />
                </label>
                <label className="block">
                  <span className="block text-xs text-white/60 mb-1">Credits</span>
                  <input type="number" min={1} value={productForm.credits ?? 0} onChange={(e) => setProductForm((f) => ({ ...f, credits: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white" />
                </label>
                <label className="block">
                  <span className="block text-xs text-white/60 mb-1">Preis (Cent)</span>
                  <input type="number" min={0} value={productForm.price_cents ?? 0} onChange={(e) => setProductForm((f) => ({ ...f, price_cents: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white" />
                </label>
                <label className="block">
                  <span className="block text-xs text-white/60 mb-1">Währung</span>
                  <input type="text" placeholder="eur" value={productForm.currency ?? 'eur'} onChange={(e) => setProductForm((f) => ({ ...f, currency: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="block text-xs text-white/60 mb-1">Stripe Price ID (optional)</span>
                  <input type="text" placeholder="price_…" value={productForm.stripe_price_id ?? ''} onChange={(e) => setProductForm((f) => ({ ...f, stripe_price_id: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white" />
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!productForm.active} onChange={(e) => setProductForm((f) => ({ ...f, active: e.target.checked }))} className="rounded" />
                  <span className="text-sm text-white/80">Aktiv (in „Credits bestellen“ sichtbar)</span>
                </label>
              </div>
              <div className="mt-3 flex gap-2">
                <PrimaryButton onClick={saveProduct} disabled={productSaving}>{productSaving ? 'Speichern…' : 'Speichern'}</PrimaryButton>
                <SecondaryButton onClick={() => setProductForm(null)}>Abbrechen</SecondaryButton>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <label className="block text-sm font-medium text-white/80">Nutzer suchen</label>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="E-Mail..."
              className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-white placeholder-white/40"
            />
            <SecondaryButton onClick={loadUsers}>Suchen</SecondaryButton>
          </div>
        </Card>
        <div className="grid gap-6 md:grid-cols-2">
          <Card className="p-4">
            <h2 className="font-semibold text-white">Nutzer</h2>
            {loading && <p className="mt-2 text-sm text-white/60">Laden…</p>}
            <ul className="mt-2 space-y-1">
              {users.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => selectUser(u)}
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm ${selected?.id === u.id ? 'bg-violet-500/30 text-white' : 'text-white/80 hover:bg-white/10'}`}
                  >
                    {u.email} – {u.credits_unlimited ? '∞' : u.credits_balance} Credits
                    {u.is_active === false && <span className="ml-2 text-amber-400">(gesperrt)</span>}
                  </button>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="p-4">
            {!selected ? (
              <p className="text-white/60">Nutzer auswählen</p>
            ) : (
              <>
                <h2 className="font-semibold text-white">{selected.email}</h2>
                {selected.is_active === false && (
                  <p className="mt-1 text-sm text-amber-400">Dieser Nutzer ist gesperrt und kann sich nicht anmelden.</p>
                )}
                {message && <p className="mt-2 text-sm text-green-400">{message}</p>}
                <div className="mt-4 space-y-3">
                  <div className="flex flex-wrap gap-2 items-center">
                    {selected.is_active === false ? (
                      <PrimaryButton onClick={toggleSperren}>Entsperren</PrimaryButton>
                    ) : (
                      <DangerButton onClick={toggleSperren}>Sperren</DangerButton>
                    )}
                    {selected.is_active === false && (
                      <span className="text-sm text-white/60">Nutzer kann sich nach Entsperren wieder anmelden.</span>
                    )}
                  </div>
                  <p className="text-sm text-white/80">Aktuell: {selected.credits_unlimited ? 'Unlimited' : selected.credits_balance} Credits</p>
                  <div className="flex flex-wrap gap-2">
                    <PrimaryButton onClick={() => applyDelta(10)}>+10</PrimaryButton>
                    <PrimaryButton onClick={() => applyDelta(50)}>+50</PrimaryButton>
                    <PrimaryButton onClick={() => applyDelta(100)}>+100</PrimaryButton>
                    <SecondaryButton onClick={() => applyDelta(-1)}>-1</SecondaryButton>
                    <SecondaryButton onClick={() => applyDelta(-10)}>-10</SecondaryButton>
                    <SecondaryButton onClick={() => applyDelta(-50)}>-50</SecondaryButton>
                  </div>
                  <div className="flex gap-2 items-center">
                    <input
                      type="number"
                      value={creditsSet}
                      onChange={(e) => setCreditsSet(e.target.value)}
                      placeholder="Auch negativ"
                      className="w-28 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white"
                    />
                    <SecondaryButton onClick={applySet}>Setzen</SecondaryButton>
                  </div>
                  <p className="text-xs text-white/40">Minus-Kontostand ist erlaubt (z. B. −5).</p>
                  <label className="flex items-center gap-2 text-sm text-white/80">
                    <input type="checkbox" checked={unlimited} onChange={toggleUnlimited} className="rounded" />
                    Unlimited
                  </label>
                </div>
                <h3 className="mt-6 font-medium text-white">Ledger (letzte Einträge)</h3>
                <ul className="mt-2 max-h-48 overflow-y-auto space-y-1 text-xs text-white/70">
                  {ledger.slice(0, 30).map((e) => (
                    <li key={e.id}>{e.delta > 0 ? '+' : ''}{e.delta} – {e.reason} – {e.created_at?.slice(0, 19)}</li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        </div>
      </div>
    </PageShell>
  )
}
